import { supabase } from '../lib/supabase';

const safe = (n) => (typeof n === 'number' && !isNaN(n) ? n : 0);

export const dashboardService = {
  async getWordpressStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { sites: 0, totalArticles: 0, generatedToday: 0, queued: 0 };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [sites, total, today_, queued] = await Promise.all([
        scoped(supabase.from('wp_sites').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
        scoped(supabase.from('wp_articles').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
        scoped(supabase.from('wp_articles').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'completed').gte('created_at', todayISO)),
        scoped(supabase.from('wp_articles').select('*', { count: 'exact', head: true }).eq('user_id', user.id).in('status', ['queued', 'generating', 'processing']))
      ]);

      return {
        sites: safe(sites.count),
        totalArticles: safe(total.count),
        generatedToday: safe(today_.count),
        queued: safe(queued.count)
      };
    } catch (err) {
      console.error('getWordpressStats:', err);
      return { sites: 0, totalArticles: 0, generatedToday: 0, queued: 0 };
    }
  },

  async getPinterestStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { totalPins: 0, publishedToday: 0, scheduled: 0, failed: 0 };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [total, published, scheduled, failed] = await Promise.all([
        scoped(supabase.from('pinterest_pins').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
        scoped(supabase.from('pinterest_pins').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'published').gte('published_at', today.toISOString())),
        scoped(supabase.from('pinterest_pins').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'scheduled')),
        scoped(supabase.from('pinterest_pins').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'failed'))
      ]);

      return {
        totalPins: safe(total.count),
        publishedToday: safe(published.count),
        scheduled: safe(scheduled.count),
        failed: safe(failed.count)
      };
    } catch (err) {
      console.error('getPinterestStats:', err);
      return { totalPins: 0, publishedToday: 0, scheduled: 0, failed: 0 };
    }
  },

  async getFacebookStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { pages: 0, publishedToday: 0, scheduled: 0, failed: 0 };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      let pagesQuery = supabase
        .from('facebook_pages')
        .select('page_id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      const { count: pages } = await scoped(pagesQuery);

      let publishedToday = 0, scheduled = 0, failed = 0;
      try {
        // fb_scheduled_posts.profile_id is nullable (a handful of pre-existing
        // rows have no owner at all — see 20260801000004) so only scope when
        // a profileId is actually provided, same as everywhere else here.
        const [p, s, f] = await Promise.all([
          scoped(supabase.from('fb_scheduled_posts').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'published').gte('created_at', today.toISOString())),
          scoped(supabase.from('fb_scheduled_posts').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'scheduled')),
          scoped(supabase.from('fb_scheduled_posts').select('*', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'failed'))
        ]);
        publishedToday = safe(p.count);
        scheduled = safe(s.count);
        failed = safe(f.count);
      } catch (_) { /* table may not exist */ }

      return { pages: safe(pages), publishedToday, scheduled, failed };
    } catch (err) {
      console.error('getFacebookStats:', err);
      return { pages: 0, publishedToday: 0, scheduled: 0, failed: 0 };
    }
  },

  async getInstagramStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [accounts, published, scheduled, failed] = await Promise.all([
        scoped(supabase.from('instagram_accounts').select('id', { count: 'exact', head: true }).eq('user_id', user.id)),
        scoped(supabase.from('instagram_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'published').gte('scheduled_at', today.toISOString())),
        scoped(supabase.from('instagram_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'scheduled')),
        scoped(supabase.from('instagram_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'failed'))
      ]);

      return {
        accounts: safe(accounts.count),
        publishedToday: safe(published.count),
        scheduled: safe(scheduled.count),
        failed: safe(failed.count)
      };
    } catch (err) {
      console.error('getInstagramStats:', err);
      return { accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 };
    }
  },

  async getThreadsStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [accounts, published, scheduled, failed] = await Promise.all([
        scoped(supabase.from('threads_accounts').select('id', { count: 'exact', head: true }).eq('user_id', user.id)),
        scoped(supabase.from('threads_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'published').gte('scheduled_at', today.toISOString())),
        scoped(supabase.from('threads_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'scheduled')),
        scoped(supabase.from('threads_scheduled_posts').select('id', { count: 'exact', head: true }).eq('user_id', user.id).eq('status', 'failed'))
      ]);

      return {
        accounts: safe(accounts.count),
        publishedToday: safe(published.count),
        scheduled: safe(scheduled.count),
        failed: safe(failed.count)
      };
    } catch (err) {
      console.error('getThreadsStats:', err);
      return { accounts: 0, publishedToday: 0, scheduled: 0, failed: 0 };
    }
  },

  async getEtsyStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { shops: 0, activeListings: 0, syncedToday: 0, lastSync: null };

      let shopsQuery = supabase
        .from('etsy_accounts')
        .select('connected_at', { count: 'exact' })
        .eq('user_id', user.id)
        .order('connected_at', { ascending: false })
        .limit(1);
      if (profileId) shopsQuery = shopsQuery.eq('profile_id', profileId);
      const { count: shops, data: shopRows, error: shopsError } = await shopsQuery;
      if (shopsError) throw shopsError;

      let insightsQuery = supabase
        .from('platform_insight_snapshots')
        .select('account_id, metric_value, metric_date, captured_at')
        .eq('user_id', user.id)
        .eq('platform', 'etsy')
        .eq('metric_name', 'active_listings')
        .order('metric_date', { ascending: false })
        .order('captured_at', { ascending: false });
      if (profileId) insightsQuery = insightsQuery.eq('profile_id', profileId);
      const { data: insightRows, error: insightError } = await insightsQuery;
      if (insightError) throw insightError;

      const latestByAccount = new Map();
      (insightRows || []).forEach((row) => {
        if (!latestByAccount.has(row.account_id)) latestByAccount.set(row.account_id, row);
      });
      const activeListings = [...latestByAccount.values()]
        .reduce((sum, row) => sum + safe(Number(row.metric_value)), 0);

      const today = new Date().toISOString().slice(0, 10);
      let syncQuery = supabase
        .from('automation_activity_history')
        .select('event_value')
        .eq('user_id', user.id)
        .eq('platform', 'etsy')
        .eq('event_type', 'synchronized')
        .gte('occurred_at', `${today}T00:00:00.000Z`);
      if (profileId) syncQuery = syncQuery.eq('profile_id', profileId);
      const { data: syncRows, error: syncError } = await syncQuery;
      if (syncError) throw syncError;

      return {
        shops: safe(shops),
        activeListings,
        syncedToday: (syncRows || []).reduce((sum, row) => sum + safe(Number(row.event_value)), 0),
        lastSync: insightRows?.[0]?.captured_at || shopRows?.[0]?.connected_at || null
      };
    } catch (err) {
      console.error('getEtsyStats:', err);
      return { shops: 0, activeListings: 0, syncedToday: 0, lastSync: null };
    }
  },

  async getRecentActivity(limit = 20, profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [wpResult, pinResult] = await Promise.allSettled([
        scoped(supabase.from('wp_articles').select('id, title, status, updated_at, created_at').eq('user_id', user.id)).order('updated_at', { ascending: false }).limit(12),
        scoped(supabase.from('pinterest_pins').select('id, pin_title, status, updated_at, created_at, published_at').eq('user_id', user.id)).order('updated_at', { ascending: false }).limit(8)
      ]);

      const activities = [];

      const wpStatusMap = {
        completed: { label: 'Article Generated', status: 'success' },
        error: { label: 'Article Failed', status: 'error' },
        processing: { label: 'Article Processing', status: 'processing' },
        generating: { label: 'Article Generating', status: 'processing' },
        queued: { label: 'Article Queued', status: 'pending' },
        draft: { label: 'Draft Saved', status: 'pending' }
      };

      const pinStatusMap = {
        published: { label: 'Pin Published', status: 'success' },
        scheduled: { label: 'Pin Scheduled', status: 'pending' },
        failed: { label: 'Pin Failed', status: 'error' },
        ready: { label: 'Pin Ready', status: 'pending' }
      };

      if (wpResult.status === 'fulfilled') {
        (wpResult.value.data || []).forEach(a => {
          const s = wpStatusMap[a.status] || { label: a.status, status: 'pending' };
          activities.push({
            id: `wp-${a.id}`,
            platform: 'wordpress',
            event: s.label,
            description: a.title || 'Untitled Article',
            status: s.status,
            timestamp: a.updated_at || a.created_at,
            icon: 'FileText'
          });
        });
      }

      if (pinResult.status === 'fulfilled') {
        (pinResult.value.data || []).forEach(p => {
          const s = pinStatusMap[p.status] || { label: p.status, status: 'pending' };
          activities.push({
            id: `pin-${p.id}`,
            platform: 'pinterest',
            event: s.label,
            description: p.pin_title || 'Untitled Pin',
            status: s.status,
            timestamp: p.published_at || p.updated_at || p.created_at,
            icon: 'Image'
          });
        });
      }

      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      return activities.slice(0, limit);
    } catch (err) {
      console.error('getRecentActivity:', err);
      return [];
    }
  },

  async getChartData(days = 7, profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);
      const startISO = startDate.toISOString();

      const scoped = (q) => (profileId ? q.eq('profile_id', profileId) : q);

      const [wpResult, pinResult] = await Promise.allSettled([
        scoped(supabase.from('wp_articles').select('created_at').eq('user_id', user.id).gte('created_at', startISO)),
        scoped(supabase.from('pinterest_pins').select('created_at').eq('user_id', user.id).gte('created_at', startISO))
      ]);

      const dateMap = {};
      for (let i = days; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().split('T')[0];
        dateMap[key] = { date: key, wordpress: 0, pinterest: 0, facebook: 0, etsy: 0 };
      }

      if (wpResult.status === 'fulfilled') {
        (wpResult.value.data || []).forEach(a => {
          const key = new Date(a.created_at).toISOString().split('T')[0];
          if (dateMap[key]) dateMap[key].wordpress++;
        });
      }

      if (pinResult.status === 'fulfilled') {
        (pinResult.value.data || []).forEach(p => {
          const key = new Date(p.created_at).toISOString().split('T')[0];
          if (dateMap[key]) dateMap[key].pinterest++;
        });
      }

      return Object.values(dateMap);
    } catch (err) {
      console.error('getChartData:', err);
      return [];
    }
  },

  async getMonthlyTokenStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { monthly: 0, monthlyTokens: 0, today: 0, todayTokens: 0, requests: 0 };

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const todayDate = now.toISOString().split('T')[0];

      const [monthRes, todayRes] = await Promise.all([
        supabase.from('token_usage').select('estimated_cost_usd, total_tokens').eq('user_id', user.id).gte('usage_date', monthStart),
        supabase.from('token_usage').select('estimated_cost_usd, total_tokens').eq('user_id', user.id).eq('usage_date', todayDate)
      ]);

      const sum = (rows, field) => (rows || []).reduce((acc, r) => acc + (parseFloat(r[field]) || 0), 0);

      return {
        monthly: sum(monthRes.data, 'estimated_cost_usd'),
        monthlyTokens: sum(monthRes.data, 'total_tokens'),
        today: sum(todayRes.data, 'estimated_cost_usd'),
        todayTokens: sum(todayRes.data, 'total_tokens'),
        requests: (monthRes.data || []).length
      };
    } catch (err) {
      console.error('getMonthlyTokenStats:', err);
      return { monthly: 0, monthlyTokens: 0, today: 0, todayTokens: 0, requests: 0 };
    }
  },

  generateAlerts(wpStats, pinStats, fbStats, jobStats, apiStats, failedJobs = [], instagramStats = {}, threadsStats = {}) {
    const alerts = [];
    const now = new Date();

    // Failed job alerts
    failedJobs.slice(0, 3).forEach((job, i) => {
      alerts.push({
        id: `failed-job-${i}`,
        severity: 'critical',
        title: 'WordPress Article Failed',
        message: job.error_message || `Article "${job.title || 'Unknown'}" failed to generate`,
        platform: 'wordpress',
        timestamp: job.created_at || now.toISOString()
      });
    });

    // Pinterest failures
    if (pinStats?.failed > 0) {
      alerts.push({
        id: 'pin-failed',
        severity: 'warning',
        title: 'Pinterest Pins Failed',
        message: `${pinStats.failed} pin(s) failed to publish. Check Pinterest connection.`,
        platform: 'pinterest',
        timestamp: now.toISOString()
      });
    }

    // Facebook failures
    if (fbStats?.failed > 0) {
      alerts.push({
        id: 'fb-failed',
        severity: 'warning',
        title: 'Facebook Posts Failed',
        message: `${fbStats.failed} post(s) failed to publish. Check page access tokens.`,
        platform: 'facebook',
        timestamp: now.toISOString()
      });
    }

    // Instagram failures
    if (instagramStats?.failed > 0) {
      alerts.push({
        id: 'instagram-failed',
        severity: 'warning',
        title: 'Instagram Posts Failed',
        message: `${instagramStats.failed} post(s) failed to publish. Check Instagram connection.`,
        platform: 'instagram',
        icon: 'Instagram',
        timestamp: now.toISOString()
      });
    }

    // Threads failures
    if (threadsStats?.failed > 0) {
      alerts.push({
        id: 'threads-failed',
        severity: 'warning',
        title: 'Threads Posts Failed',
        message: `${threadsStats.failed} post(s) failed to publish. Check Threads connection.`,
        platform: 'threads',
        icon: 'AtSign',
        timestamp: now.toISOString()
      });
    }

    // API errors
    if (apiStats?.byService) {
      Object.entries(apiStats.byService).forEach(([service, data]) => {
        if (data?.errors > 2) {
          alerts.push({
            id: `api-${service}`,
            severity: 'warning',
            title: `${service.charAt(0).toUpperCase() + service.slice(1)} API Errors`,
            message: `${data.errors} errors detected. Some requests may have failed.`,
            platform: service,
            timestamp: now.toISOString()
          });
        }
      });
    }

    // Queue backup
    if (jobStats?.pending > 20) {
      alerts.push({
        id: 'queue-backup',
        severity: 'info',
        title: 'Queue Backup',
        message: `${jobStats.pending} jobs pending. Processing may be delayed.`,
        platform: 'wordpress',
        timestamp: now.toISOString()
      });
    }

    return alerts;
  }
};
